/**
 * DashboardPage — the authenticated customer portal.
 *
 * Shown after OTP login (route: /dashboard). Greets the customer and lists
 * their full report history (newest first). Clicking a report opens the full
 * report view at /reports/:reportId. The header replaces the public navbar:
 * it shows the signed-in customer's name + phone and a logout action.
 */
import { useEffect, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import { ChevronDown, ChevronRight, FileText, Loader2, LogOut, Search, Settings } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '../stores/authStore'
import { customerAuthApi } from '../api/client'
import type { CustomerReportSummary } from '../types'
import { useI18nNavigate } from '../hooks/useI18nNavigate'
import { LanguageSwitcher } from '../components/LanguageSwitcher'
import { ScoreRing } from '../components/dashboard/ScoreRing'
import { APP_NAME, BRAND } from '../config'
import logoHeader from '../assets/landing/logo-header.svg'

export function DashboardPage() {
  const navigate = useI18nNavigate()
  const { t, i18n } = useTranslation('dashboard')
  const { t: tCommon } = useTranslation('common')
  const { customer, loadCustomer, logout } = useAuthStore()

  const [reports, setReports] = useState<CustomerReportSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Auth guard: redirect to login if there is no session.
  useEffect(() => {
    const token = localStorage.getItem('access_token')
    if (!token) {
      navigate('/login')
      return
    }
    // Ensure the customer profile is loaded (e.g. after a hard refresh).
    if (!customer) loadCustomer()
  }, [customer, loadCustomer, navigate])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false)
      }
    }

    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [dropdownOpen])

  // Load report history.
  useEffect(() => {
    let active = true
    customerAuthApi
      .listReports()
      .then((items) => { if (active) setReports(items ?? []) })
      .catch(() => { if (active) setError(t('error.load')) })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [t])

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  const displayName = customer?.first_name ? `${customer.first_name}${customer.last_name ? ' ' + customer.last_name : ''}`.trim() : customer?.phone || ''
  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleDateString(i18n.language, {
        year: 'numeric', month: 'short', day: 'numeric',
      })
    } catch { return iso }
  }

  return (
    <div className="min-h-screen bg-[#fafaf8]">
      {/* ── Header (signed-in navbar) ──────────────────────────────────────── */}
      <header className="sticky top-0 z-50 bg-white shadow-[0px_4px_2px_rgba(0,0,0,0.08)]">
        <div className="mx-auto flex min-h-[72px] max-w-5xl items-center justify-between gap-4 px-5 py-3 sm:px-6">
          <button type="button" onClick={() => navigate('/')} className="shrink-0">
            <img src={logoHeader} alt={APP_NAME} className="h-10 w-auto sm:h-11" />
          </button>

          <div className="flex items-center gap-3">
            <LanguageSwitcher variant="compact" />

            {/* Customer dropdown menu */}
            <div ref={dropdownRef} className="relative">
              <button
                type="button"
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-2.5 rounded-full border border-gray-200 bg-white py-1 pl-1 pr-3 transition-colors hover:bg-gray-50"
              >
                <span
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold text-white"
                  style={{ backgroundColor: BRAND.navy }}
                >
                  {(displayName[0] || '?').toUpperCase()}
                </span>
                <div className="hidden leading-tight sm:block">
                  <p className="max-w-[160px] truncate text-sm font-medium text-gray-900">{displayName}</p>
                  {customer?.phone && (
                    <p className="text-xs text-gray-400">{customer.phone}</p>
                  )}
                </div>
                <ChevronDown className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Dropdown menu */}
              {dropdownOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.15 }}
                  className="absolute right-0 mt-2 w-48 rounded-lg border border-gray-200 bg-white shadow-lg"
                >
                  <button
                    type="button"
                    onClick={() => {
                      navigate('/dashboard/profile')
                      setDropdownOpen(false)
                    }}
                    className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 first:rounded-t-lg"
                  >
                    <Settings className="h-4 w-4" />
                    {t('profile.settings', 'Profile Settings')}
                  </button>
                </motion.div>
              )}
            </div>

            <button
              type="button"
              onClick={() => navigate('/')}
              aria-label={tCommon('nav.search')}
              className="inline-flex h-9 items-center justify-center gap-1.5 rounded-[10px] border border-gray-200 bg-white px-3 text-sm font-medium text-[#4a5565] transition-colors hover:bg-[#f2f2f6]"
            >
              <Search className="h-4 w-4 shrink-0" />
              <span className="hidden sm:inline">{tCommon('nav.search')}</span>
            </button>
            <button
              type="button"
              onClick={handleLogout}
              className="inline-flex h-9 items-center justify-center gap-1.5 rounded-[10px] border border-gray-200 bg-white px-3 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">{t('logout', 'Log out')}</span>
            </button>
          </div>
        </div>
      </header>

      {/* ── Body ───────────────────────────────────────────────────────────── */}
      <main className="mx-auto max-w-3xl px-5 py-10 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 text-center"
        >
          <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">
            {t('welcome', { name: displayName })}
          </h1>
          <p className="mt-1.5 text-sm text-gray-500">
            {t('reportCount', { count: reports.length })}
          </p>
        </motion.div>

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : error ? (
          <div className="rounded-2xl border border-red-100 bg-red-50 p-6 text-center text-sm text-red-700">
            {error}
          </div>
        ) : reports.length === 0 ? (
          <EmptyState onScan={() => navigate('/')} />
        ) : (
          <div className="space-y-6">
            {/* Latest report — featured */}
            <section>
              <h2 className="mb-2.5 px-1 text-sm font-semibold text-gray-500">
                {t('section.latest', 'Latest')}
              </h2>
              <ReportCard
                report={reports[0]}
                featured
                onClick={() => navigate(`/reports/${reports[0].id}`)}
                formatDate={formatDate}
              />
            </section>

            {/* The rest */}
            {reports.length > 1 && (
              <section>
                <h2 className="mb-2.5 px-1 text-sm font-semibold text-gray-500">
                  {t('section.all', 'All your reports')}
                </h2>
                <div className="space-y-3">
                  {reports.slice(1).map((r, i) => (
                    <motion.div
                      key={r.id}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: Math.min(i * 0.03, 0.3) }}
                    >
                      <ReportCard
                        report={r}
                        onClick={() => navigate(`/reports/${r.id}`)}
                        formatDate={formatDate}
                      />
                    </motion.div>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </main>
    </div>
  )
}

// ─── Report card ───────────────────────────────────────────────────────────────

interface ReportCardProps {
  report: CustomerReportSummary
  featured?: boolean
  onClick: () => void
  formatDate: (iso: string) => string
}

function ReportCard({ report, featured, onClick, formatDate }: ReportCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`group flex w-full items-center gap-4 rounded-2xl border bg-white p-4 text-left transition-all hover:shadow-md ${
        featured ? 'border-gray-200 shadow-sm' : 'border-gray-100'
      }`}
      style={featured ? { borderColor: BRAND.navy } : undefined}
    >
      {/* Thumbnail */}
      <div className="relative h-12 w-12 shrink-0 overflow-hidden rounded-xl bg-gray-100">
        {report.business_logo ? (
          <img
            src={report.business_logo}
            alt={report.business_name}
            className="h-full w-full object-cover"
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
          />
        ) : (
          <span className="flex h-full w-full items-center justify-center text-gray-300">
            <FileText className="h-5 w-5" />
          </span>
        )}
      </div>

      {/* Name + meta */}
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-gray-900">
          {report.business_name || '—'}
        </p>
        <p className="mt-0.5 text-xs text-gray-400">{formatDate(report.created_at)}</p>
      </div>

      {/* Score + chevron */}
      <ScoreRing score={report.overall_score} />
      <ChevronRight className="h-5 w-5 shrink-0 text-gray-300 transition-colors group-hover:text-gray-500" />
    </button>
  )
}

// ─── Empty state ───────────────────────────────────────────────────────────────

function EmptyState({ onScan }: { onScan: () => void }) {
  const { t } = useTranslation('dashboard')
  return (
    <div className="rounded-2xl border border-gray-100 bg-white py-16 text-center shadow-sm">
      <span className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-gray-50 text-gray-300">
        <FileText className="h-6 w-6" />
      </span>
      <p className="mb-1 text-base font-semibold text-gray-900">{t('empty.title', 'No reports yet')}</p>
      <p className="mb-6 text-sm text-gray-500">{t('empty.subtitle', 'Run your first scan to see it here.')}</p>
      <button
        type="button"
        onClick={onScan}
        className="inline-flex items-center justify-center gap-2 rounded-xl px-5 py-2.5 text-sm font-medium text-white transition-colors"
        style={{ backgroundColor: BRAND.red }}
        onMouseOver={(e) => { e.currentTarget.style.backgroundColor = BRAND.redHover }}
        onMouseOut={(e) => { e.currentTarget.style.backgroundColor = BRAND.red }}
      >
        <Search className="h-4 w-4" />
        {t('empty.cta', 'Scan a business')}
      </button>
    </div>
  )
}
